import {
  ButtonGroup,
  TextField,
  Typography
} from "@material-ui/core";
import clsx from "clsx";
import _ from "lodash";
import React, { useState, useMemo } from "react";
import { t } from "ttag";
import { Button, IconButton, Select } from '@coolremote/shared';
import { Close, Plus } from "../../icons";
import Tooltip from "../Tooltip/LightTooltip";
import styles from "./AddRule.style";

const CustomSelect = ({ disableTooltip = false, notDisabledOptions, className, style, error, disabled, options = [], value, placeholder, onChange, optionValue = "value", label = "name", name, height }: any) => {

  const classes = styles();
  const selectedValue = options.find((option: any) => String(option[optionValue]) === String(value));
  const valueName = selectedValue?.[label];

  const mappedOptions = _.isEmpty(options)
    ? [{ value: "none", label: t`No Options`, disabled: true }]
    : options
        .filter((option: any) => !notDisabledOptions || !notDisabledOptions.includes(option[optionValue]))
        .map((option: any) => ({ value: option[optionValue], label: option[label], disabled: option.disabled }));

  const isSelectDisabled = disabled || !Array.isArray(options) || options.length === 0;

  return (
    <Tooltip disableTooltip={disableTooltip} placement="bottom" title={valueName || ""}>
      <div className={clsx(classes.selectContainer, { [className]: className })} style={style}>
        <Select
          fullWidth    
          menuPortal
          floatingLabel
          label={placeholder}
          disabled={isSelectDisabled}
          value={value === undefined ? "" : (value || "")}
          onChange={(val: any) => onChange({ target: { value: val, name } })}
          error={!!error}
          options={mappedOptions}
          classes={{ trigger: clsx({ [classes.disabledSelect]: isSelectDisabled }), option: classes.optionStyle }}
          height={height}
        />
      </div>
    </Tooltip>
  );
};

const tooltipWrapper = (tooltip: string | JSX.Element, component: React.ReactElement) => {
  if (tooltip) {
    return (
      <Tooltip title={tooltip}>
        <div>
          {component}
        </div>
      </Tooltip>
    );
  } else {
    return component;
  }
};

const Box = ({ title, children, small, bLabel1, bLabel2, bDisabled1, bDisabled2, action1, action2, className, addBottomMargin, bTooltip1, bTooltip2 }: any) => {

  const classes = styles();

  return <div id="box1" className={clsx(classes.boxContainer, { [classes.small]: small, [classes.addBottomMargin]: addBottomMargin, [className]: className })}>
    <div id="box-header1" className={classes.boxHeader}>
      <Typography className={classes.boxTitle}>{title}</Typography>
      <div className={classes.buttonsContainer}>
        {action1 && 
        tooltipWrapper(
        bTooltip1,
        <Button
          white
          width={small ? 130 : 144}
          className={clsx(classes.btn, classes.addMargin13, { [classes.disabledWhiteButton]: bDisabled1 })}
          startIcon={<Plus className={classes.plusIcon} />}
          disabled={bDisabled1}
          onClick={action1}
          onMouseDown={(event: any) => { event.preventDefault(); }}
        >
          {bLabel1}
        </Button>
        )}
        { 
        tooltipWrapper(
          bTooltip2,
          <Button 
            onMouseDown={(event: any) => { event.preventDefault(); }} 
            white 
            width={110} 
            className={clsx(classes.btn, { [classes.disabledWhiteButton]: bDisabled2 })} 
            disabled={bDisabled2} onClick={action2}
            startIcon={small ? <Plus className={classes.plusIcon} /> : undefined} 
            >
              {bLabel2}
            </Button>
        )}
      </div>
    </div>
    <div className={clsx(classes.boxContent, { [classes.unitsAppliedBox]: small })}>{children}</div>
  </div >;
};

const CustomedTextField = ({ maxLength = "", error, label, disabled, placeholder, value, width, normalMargin, className, onChange, name, type = "string" }: any, props: any) => {

  const classes = styles();

  return <TextField
    label={label}
    disabled={disabled}
    classes={{ root: classes.label }}
    name={name}
    value={value}
    style={width ? { width } : undefined}
    onChange={onChange}
    placeholder={placeholder}
    variant="outlined"
    error={error}
    className={clsx(classes.textField, { [classes.addMargin]: !normalMargin, [className]: className })}
    type={type}
    inputProps={{ maxLength }}
  />;
};


const UnitWrapper = ({ onRemove, systemName, unitName, disabled = false }: any) => {

  const classes = styles();

  return <div className={classes.wrapperContainer}>
    <Tooltip arrow interactive placement="top-start" title={systemName + " /" + unitName}>
      <Typography className={classes.namesContainer}><span className={classes.name}>{systemName || ""}</span><span className={classes.middle}>/</span> <span className={classes.name}>{unitName || ""}</span></Typography>
    </Tooltip>
    <IconButton disableRipple disabled={disabled} className={classes.removeButton} onClick={onRemove}><Close color="#fff" /></IconButton></div>;
};

const SystemWrapper = ({ onRemove, systemName, disabled = false }: any) => {

  const classes = styles();

  return <div className={classes.wrapperContainer}>
    <Tooltip arrow interactive placement="top-start" title={systemName} >
      <Typography className={classes.namesContainer}><span className={classes.name}>{systemName}</span></Typography>
    </Tooltip>
    <IconButton disableRipple disabled={disabled} className={classes.removeButton} onClick={onRemove}><Close color="#fff" /></IconButton></div>;
};

const TextInput = ({
  id,
  label,
  error,
  className,
  onChange,
  ...props
}: any) => {
  const classes = clsx(
    "input-group",
    {
      "animated shake error": !!error
    }
    // className
  );
  return (
    <div className={classes}>
      <label htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="text-input"
        onChange={onChange}
        {...props}
      />
      <p>{error}</p>
    </div>
  );
};

const CustomAutoComplete = ({ modifyOptions, useTooltip = false, tooltipProp = "", notDisabledOptions, className, error, disabled, options = [], value = "", placeholder, onChange, optionValue = "value", label = "name", name, customTooltip }: any) => {

  const classes = styles();

  const processedOptions = useMemo(() => {
    return modifyOptions ? modifyOptions(options) : options;
  }, [options, modifyOptions]);

  const mappedOptions = useMemo(() => {
    return processedOptions.map((option: any) => ({
      value: option[optionValue],
      label: option[label],
      disabled: option.disabled,
    }));
  }, [processedOptions, optionValue, label]);

  const currentValue = value?.[optionValue] ?? (typeof value === "string" ? value : "");
  const tooltipTitle = customTooltip || (value && tooltipProp ? value[tooltipProp] : "");

  return (
    <Tooltip placement="top" title={tooltipTitle}>
      <div className={clsx(classes.autoComplete, className)}>
        <Select
          fullWidth
          menuPortal
          searchable
          floatingLabel
          disabled={disabled}
          value={currentValue}
          onChange={(val: any) => {
            const found = processedOptions.find((o: any) => String(o[optionValue]) === String(val));
            onChange(null, found || null);
          }}
          error={!!error}
          options={mappedOptions}
          label={placeholder}
          classes={{ trigger: clsx(classes.autoCompleteTrigger, { [classes.disabledSelect]: disabled }), option: classes.optionStyle }}
        />
      </div>
    </Tooltip>
  );
};

export { CustomAutoComplete, CustomSelect, Box, CustomedTextField, SystemWrapper, UnitWrapper, TextInput };
